"use client";

import DropLine from "@/components/DropLine";
import FooterButton from "@/components/FooterButton";
import DiscordIcon from "@/components/image-component/discord-icon";
import XIcon from "@/components/image-component/x-icon";
import PrimaryButton from "@/components/PrimaryButton";
import Image from "next/image";
import { PropsWithChildren } from "react";
import {
  Blobbie,
  useActiveAccount,
  useActiveWallet,
  useAutoConnect,
  useConnectModal,
  useDisconnect,
  useWalletDetailsModal,
} from "thirdweb/react";
import { shortenAddress, signMessage } from "thirdweb/utils";
import { createWallet } from "thirdweb/wallets";
import { chain, thirdWebClient } from "./libs/thirdweb";
import { termsMessage } from "@/utils/terms-message";
import { connectWallet } from "@/services/connect-wallet";
// import { useRouter } from "next/navigation";
// import { STAKING_APP_URL } from "@/config";

const wallets = [
  createWallet("io.metamask"),
  createWallet("com.coinbase.wallet"),
  createWallet("me.rainbow"),
  createWallet("walletConnect"),
];

export function LayoutClient({ children }: PropsWithChildren) {
  const { connect, isConnecting } = useConnectModal();
  const { disconnect } = useDisconnect();
  const wallet = useActiveWallet();
  // const router = useRouter();

  const activeAccount = useActiveAccount();
  const detailsModal = useWalletDetailsModal();

  useAutoConnect({
    client: thirdWebClient,
    wallets,
  });

  const onClick = async () => {
    if (isConnecting) return;

    const wallet = await connect({
      client: thirdWebClient,
      wallets,
      size: "compact",
    });
    await wallet?.switchChain(chain);
    const walletAccount = wallet.getAccount();
    try {
      if (walletAccount) {
        const signature = await signMessage({
          message: termsMessage,
          account: walletAccount,
        });
        const address = walletAccount.address;
        await connectWallet({ address, signature });
      }
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((error as any)?.code === 4001) {
        if (wallet) {
          disconnect(wallet);
          return;
        }
      }
    }
  };

  const handleClick = () => {
    detailsModal.open({ client: thirdWebClient, theme: "dark" });
  };

  const twitterText = process.env.NEXT_PUBLIC_TWITTER_TEXT || "62.5K";
  const discordText = process.env.NEXT_PUBLIC_DISCORD_TEXT || "47K";
  // const githubText = process.env.NEXT_PUBLIC_GITHUB_TEXT || "196";
  // const linkedinText = process.env.NEXT_PUBLIC_LINKEDIN_TEXT || "7K";

  const footerButtons = [
    {
      id: 1,
      title: "X",
      text: twitterText,
      onClick: () => window.open("https://x.com/spheron"),
      icon: <XIcon />,
    },
    {
      id: 2,
      title: "Dicord",
      text: discordText,
      onClick: () => window.open("https://sphn.wiki/discord"),
      icon: <DiscordIcon />,
    },
    // {
    //   id: 2,
    //   title: "Github",
    //   text: githubText,
    //   onClick: () => window.open("https://github.com/spheronFdn"),
    //   icon: <GithubIcon />,
    // },
    // {
    //   id: 3,
    //   title: "LinkedIn",
    //   text: linkedinText,
    //   onClick: () => window.open("https://sphn.wiki/linkedin"),
    //   icon: <LinkedinIcon />,
    // },
  ];

  return (
    <main className="bg-transparent h-screen overflow-auto scroll-smooth">
      <div className="hidden lg:block lg:absolute top-0 left-[4%] h-screen">
        <DropLine startPos={100} duration="10s" />
      </div>
      <div className="hidden lg:block lg:absolute top-0 right-[4%] h-screen">
        <DropLine startPos={2} duration="10s" />
      </div>
      <div className="hidden lg:block lg:absolute top-0 left-[25%] h-screen">
        <DropLine startPos={400} duration="7s" />
      </div>
      <div className="hidden lg:block lg:absolute top-0 right-[25%] h-screen">
        <DropLine startPos={250} duration="8s" />
      </div>
      <div className="lg:min-h-[calc(100vh-80px)]">
        <div className="flex flex-row justify-between px-4 lg:px-[74px] py-3 h-[80px]">
          <div
            className="cursor-pointer"
            onClick={() => {
              window.open("https://spheron.network");
            }}
          >
            <Image
              src="/spheron-logo.svg"
              alt="spheron logo"
              width="100"
              height="34"
            />
          </div>
          <div className="flex items-center gap-6">
            {/* <div
              onClick={() => router.push("#buy-spon")}
              className="flex items-center gap-10 font-jetbrans-mono text-white text-base hover:underline cursor-pointer"
            >
              BUY SPON
            </div> */}
            {/* <div
              className="flex items-center gap-10 font-jetbrans-mono text-white text-base hover:underline cursor-pointer"
              onClick={() => {
                window.open(STAKING_APP_URL, "_blank");
              }}
            >
              STAKE SPON
            </div> */}

            {activeAccount?.address && (
              <div
                onClick={handleClick}
                className={`hidden lg:flex gap-x-2 items-center text-white text-base font-normal font-jetbrans-mono leading-none cursor-pointer px-4 py-3`}
              >
                <Blobbie
                  style={{
                    borderRadius: "50%",
                  }}
                  size={32}
                  address={activeAccount.address}
                />
                <span>{shortenAddress(activeAccount.address)}</span>
                <span className="opacity-30 text-white">|</span>
                <button
                  className="uppercase hover:underline"
                  onClick={() => {
                    if (wallet) {
                      localStorage.removeItem(
                        `sig-${activeAccount.address.toLowerCase()}`
                      );
                      disconnect(wallet);
                    }
                  }}
                >
                  Disconnect
                </button>
              </div>
            )}
            {!activeAccount?.address && (
              <div className="lg:w-[18vw] hidden lg:hidden grow-0 relative h-full">
                <PrimaryButton
                  variant="dark"
                  className="rounded-tr-md"
                  onClick={() => {
                    onClick();
                  }}
                >
                  Connect Your Wallet
                </PrimaryButton>
              </div>
            )}
          </div>
        </div>
        <div className="mx-auto lg:min-w-[960px] lg:w-[45vw] w-[90vw] max-w-[850px] flex flex-col items-center">
          {children}
        </div>
      </div>
      <footer className="mt-[100px] lg:px-[74px] px-4 w-full py-4">
        <div
          className="flex flex-col gap-3 items-center lg:flex-row lg:justify-between
        space-y-2 font-normal text-xs font-sans text-white
        w-content-mobile lg:w-content"
        >
          <div>
            {/* <a
          href="./Spheron_Website_Terms_of_Use.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:underline"
        >
          Privacy Policy
        </a>{" "}
        .{" "} */}
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
            >
              Terms of Conditions
            </a>
          </div>
          <div className="flex flex-row gap-2">
            {footerButtons.map((buttonContent) => (
              <FooterButton
                key={buttonContent.id}
                onClick={() => {
                  buttonContent.onClick();
                }}
                className="w-max text-protocolNew-base
              hover:bg-protocolNew-base hover:text-white 
              duration-300 ease-out"
              >
                <div className="flex gap-2 items-center text-xs font-normal">
                  {buttonContent.icon}
                  <span>{buttonContent.text}</span>
                </div>
              </FooterButton>
            ))}
          </div>
        </div>
      </footer>
    </main>
  );
}
